"use client";

import { useCallback, useEffect, useState } from "react";

const MAX_FAILED_ATTEMPTS = 5;
const DEFAULT_COOLDOWN_SECONDS = 30;

interface UseLoginCooldownResult {
  isCoolingDown: boolean;
  remainingSeconds: number;
  recordFailedAttempt: (serverCooldownSeconds?: number) => void;
  resetFailedAttempts: () => void;
}

export function useLoginCooldown(): UseLoginCooldownResult {
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  useEffect(() => {
    if (cooldownEndsAt === null) {
      setRemainingSeconds(0);
      return;
    }

    const updateRemainingSeconds = () => {
      const remaining = Math.max(
        0,
        Math.ceil((cooldownEndsAt - Date.now()) / 1000)
      );
      setRemainingSeconds(remaining);
      if (remaining === 0) {
        setCooldownEndsAt(null);
      }
    };

    updateRemainingSeconds();
    const intervalId = window.setInterval(updateRemainingSeconds, 1000);
    return () => window.clearInterval(intervalId);
  }, [cooldownEndsAt]);

  const recordFailedAttempt = useCallback((serverCooldownSeconds?: number) => {
    if (serverCooldownSeconds !== undefined) {
      const cooldownSeconds = Math.max(
        serverCooldownSeconds,
        DEFAULT_COOLDOWN_SECONDS
      );
      setFailedAttempts(0);
      setRemainingSeconds(cooldownSeconds);
      setCooldownEndsAt(Date.now() + cooldownSeconds * 1000);
      return;
    }

    setFailedAttempts((currentAttempts) => {
      const nextAttempts = currentAttempts + 1;
      if (nextAttempts >= MAX_FAILED_ATTEMPTS) {
        setRemainingSeconds(DEFAULT_COOLDOWN_SECONDS);
        setCooldownEndsAt(Date.now() + DEFAULT_COOLDOWN_SECONDS * 1000);
        return 0;
      }
      return nextAttempts;
    });
  }, []);

  const resetFailedAttempts = useCallback(() => {
    setFailedAttempts(0);
    setRemainingSeconds(0);
    setCooldownEndsAt(null);
  }, []);

  return {
    isCoolingDown: remainingSeconds > 0,
    remainingSeconds,
    recordFailedAttempt,
    resetFailedAttempts,
  };
}
